// Data Upload: a documents column of the sheet names the files that belong to each row
// ("notice.pdf" or "notice.pdf, invoice.xlsx"). When those files are selected together with
// the sheet, they are matched by name, the same way images are.
const ALLOWED = [".pdf", ".xls", ".xlsx", ".csv"];
const MAX_SIZE = 10 * 1024 * 1024;

const clean = (name) => String(name).replace(/\s+/g, " ").trim().toLowerCase();
const extensionOf = (name) => (name.lastIndexOf(".") > 0 ? name.slice(name.lastIndexOf(".")).toLowerCase() : "");
const baseOf = (name) => (name.lastIndexOf(".") > 0 ? name.slice(0, name.lastIndexOf(".")) : name);

const namesIn = (cell) =>
  typeof cell === "string" && cell.trim() !== "0"
    ? cell
        .split(/[,;\n]+/)
        .map((name) => name.trim())
        .filter(Boolean)
    : [];

// The exact name. Or, when the sheet gives no usable extension (none, or a mistyped ".exl"), the only
// selected file with that name before the extension. "report.pdf" never matches "report.xlsx".
const findFile = (name, files) => {
  const exact = files.find((file) => clean(file.name) === clean(name));
  if (exact || ALLOWED.includes(extensionOf(name))) return exact || null;

  const sameBase = files.filter((file) => clean(baseOf(file.name)) === clean(baseOf(name)));
  return sameBase.length === 1 ? sameBase[0] : null;
};

const problemWith = (file) => {
  if (!ALLOWED.includes(extensionOf(file.name))) return "is not a PDF, XLS, XLSX or CSV file";
  if (file.size > MAX_SIZE) return "is larger than 10 MB";
  return "";
};

// Which selected files go to which row and column. Cells are emptied here; uploadSheetDocuments fills them.
// Returns { uploads: [{ row, key, files }], used: Set<File>, problems: [text] }.
export const planSheetDocuments = (rows, columns, selectedFiles, dataSheet) => {
  const files = selectedFiles.filter((file) => file !== dataSheet);
  const plan = { uploads: [], used: new Set(), problems: [] };

  rows.forEach((row, index) => {
    columns.forEach(({ key, name, maxFiles }) => {
      const names = namesIn(row[key]);
      row[key] = [];
      if (!names.length) return;

      const matched = [];
      names.forEach((fileName) => {
        const file = findFile(fileName, files);
        const problem = file ? problemWith(file) : "was not among the selected files";
        if (problem) plan.problems.push(`Row ${index + 1}, ${name}: ${fileName} ${problem}`);
        else if (!matched.includes(file)) matched.push(file);
      });

      if (matched.length > maxFiles) {
        plan.problems.push(`Row ${index + 1}, ${name}: only ${maxFiles} file${maxFiles === 1 ? "" : "s"} allowed, ${matched.length - maxFiles} left out`);
      }
      const kept = matched.slice(0, maxFiles);
      kept.forEach((file) => plan.used.add(file));
      if (kept.length) plan.uploads.push({ row, key, name, index, files: kept });
    });
  });

  return plan;
};

// Uploads each row's files and puts the result in the row. `onProgress` is called after every row.
export const uploadSheetDocuments = async (plan, company, onProgress) => {
  let attached = 0;

  for (const { row, key, name, index, files } of plan.uploads) {
    const form = new FormData();
    form.append("company", company);
    files.forEach((file) => form.append("files", file));

    try {
      const response = await window.axios.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      row[key] = response.data.map(({ _id, originalName, url }) => ({ _id, originalName, url }));
      attached += row[key].length;
    } catch (error) {
      plan.problems.push(`Row ${index + 1}, ${name}: ${error.response?.data?.message || "upload failed"}`);
    }
    onProgress();
  }

  return attached;
};

const titleKey = (title) => clean(title ?? "").replace(/[^a-z0-9]/g, "");

// How many header cells of each sheet are columns this upload expects (the platform's columns and the
// company's custom columns, documents columns included). Best match first.
export const rankSheets = (sheets, expectedTitles) => {
  const wanted = new Set(expectedTitles.map(titleKey));

  return sheets
    .map((sheet) => ({ ...sheet, score: new Set((sheet.rows[0] || []).map(titleKey).filter((title) => wanted.has(title))).size }))
    .sort((a, b) => b.score - a.score);
};
